import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AppBreadcrumbService } from 'src/app/app.breadcrumb.service';
import { Pdf } from 'src/app/clases/pdf';
import { Empresa } from 'src/app/models/configuracion/empresa.model';
import { HistorialNotificacion } from 'src/app/models/notificacion/historial_notificacion.model';
import { ProcesoReporte } from 'src/app/pages/base/proceso-reporte';
import { EmpresaService } from 'src/app/services/configuracion/empresa.service';
import { HistorialNotificacionService } from 'src/app/services/notificacion/historial_notificacion.service';
import { ReporteService } from 'src/app/services/reporte.service';
import { JobService } from 'src/app/services/notificacion/job.service';
import { XlsxService } from 'src/app/services/utils/xlsx.service';
import { Global } from 'src/global';

interface InformeNotificacionItem {
    id?: number;
    fecha: Date | string;
    nombre: string;
    descripcion: string;
    canal: string;
    origen: string;
    codigo_usuario: string;
    total_enviado: number | string;
    total_no_enviado: number | string;
    total_pendiente: number | string;
    costo_unitario: number | string;
    monto_total: number | string;
    tipo?: number | string;
    detalle_disponible?: boolean;
    cod_creado_por?: string;
    creado_por?: InformeNotificacionUsuario;
}

const DETALLE_ESTADO = {
    TODOS: 'todos',
    ENVIADOS: 'enviados',
    NO_ENVIADOS: 'no_enviados',
    PENDIENTES: 'pendientes',
} as const;

type DetalleEstado = (typeof DETALLE_ESTADO)[keyof typeof DETALLE_ESTADO];

interface InformeNotificacionDetalleItem {
    destinatario: string;
    telefono: string;
    destinatario_telefono: string;
    estado: DetalleEstado | string;
    estado_negocio: string;
    sid: string;
    status_proveedor: string;
    error: string;
    fecha: Date | string | null;
    // Campania que produjo este mensaje. Es lo que permite reenviarle con los
    // mismos parametros con los que se le iba a mandar.
    job_procesamiento_id: number | null;
    id_empresa: number | null;
}

interface InformeNotificacionUsuario {
    codigo?: string;
}

interface InformeNotificacionGraficoSerie {
    type: 'bar' | 'line';
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
    borderRadius?: number;
    tension?: number;
    fill?: boolean;
    pointRadius?: number;
    pointHoverRadius?: number;
    pointBackgroundColor?: string;
    maxBarThickness?: number;
    yAxisID: string;
}

interface InformeNotificacionGraficoData {
    labels: string[];
    datasets: InformeNotificacionGraficoSerie[];
}

@Component({
  selector: 'app-informe-notificacion',
  templateUrl: './informe-notificacion.component.html',
  styleUrls: ['./informe-notificacion.component.scss']
})
export class InformeNotificacionComponent extends ProcesoReporte implements OnInit {

    private readonly informeNotificacionStorageKey = 'informe_notificacion';

    tipos = HistorialNotificacion.TIPOS;
    totalEnviado: number;
    totalNoEnviado: number;
    totalPendiente: number;
    totalImporte: number;
    chartData: InformeNotificacionGraficoData | null;
    chartOptions: Record<string, unknown> | null;
    showDialogDetalle: boolean;
    loadingDetalle: boolean;
    detalleItems: InformeNotificacionDetalleItem[];
    /** Fallidos marcados para reenviar. */
    seleccionReenvio: InformeNotificacionDetalleItem[] = [];
    reenviando = false;
    detalleTotal: number;
    selectedInformeDetalle: InformeNotificacionItem | null;
    detalleEstado: DetalleEstado;
    detalleBusqueda: string;
    detalleFormGroup: FormGroup;
    detalleEstadoOptions = [
        { label: 'Todos', value: DETALLE_ESTADO.TODOS },
        { label: 'Enviados', value: DETALLE_ESTADO.ENVIADOS },
        { label: 'No enviados', value: DETALLE_ESTADO.NO_ENVIADOS },
        { label: 'Pendientes', value: DETALLE_ESTADO.PENDIENTES },
    ];
    constructor(
        public messageService: MessageService,
        private fb: FormBuilder,
        public service: HistorialNotificacionService,
        private router: Router,
        private breadcrumbService: AppBreadcrumbService,
        private datePipe: DatePipe,
        private empresaService: EmpresaService,
        private reporteService: ReporteService,
        private xlsxService: XlsxService,
        private jobService: JobService,
    ) {
        super();
        this.breadcrumbService.setItems([{ label: 'Notificacion' }, { label: 'Reportes' },  { label: 'Informe de Notificaciones' } ]);
        this.totalEnviado = 0;
        this.totalNoEnviado = 0;
        this.totalPendiente = 0;
        this.totalImporte = 0;
        this.chartData = null;
        this.chartOptions = null;
        this.showDialogDetalle = false;
        this.loadingDetalle = false;
        this.detalleItems = [];
        this.detalleTotal = 0;
        this.selectedInformeDetalle = null;
        this.detalleEstado = DETALLE_ESTADO.TODOS;
        this.detalleBusqueda = '';
        this.detalleFormGroup = this.fb.group({
            estado: [DETALLE_ESTADO.TODOS],
            busqueda: [''],
        });
    }

    ngOnInit(): void {
        void this.crearFormulario();
    }

    async crearFormulario() {
        const filtros = await this.reporteService.getFiltroStorage(this.informeNotificacionStorageKey);
        const hoy = new Date();
        let fechaDesde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        let fechaHasta = hoy;
        let idEmpresa = this.currentUser.id_empresa;
        let nombreEmpresa = this.currentUser.nombre_empresa;
        let tipo = HistorialNotificacion.TIPO_WHATSAPP;

        if (filtros) {
            if (filtros.fecha_desde) {
                fechaDesde = new Date(filtros.fecha_desde);
            }

            if (filtros.fecha_hasta) {
                fechaHasta = new Date(filtros.fecha_hasta);
            }

            if (Number(filtros.id_empresa) > 0) {
                idEmpresa = Number(filtros.id_empresa);
            }

            if (filtros.nombre_empresa) {
                nombreEmpresa = filtros.nombre_empresa;
            }

            if (this.isValidNotificationType(filtros.tipo)) {
                tipo = Number(filtros.tipo);
            }
        }

        this.formGroup = this.fb.group({
            fecha_desde: [fechaDesde],
            fecha_hasta: [fechaHasta],
            id_empresa: [idEmpresa],
            nombre_empresa: [nombreEmpresa],
            tipo: [tipo],
        });

        if (filtros) {
            await this.reporteService.setFiltroStorage(this.informeNotificacionStorageKey, null);
            await this.ejecutar();
        }
    }

    
    async ejecutar(){
        this.items = [];
        this.totalEnviado = 0;
        this.totalNoEnviado = 0;
        this.totalPendiente = 0;
        this.totalImporte = 0;
        this.chartData = null;
        const value = this.formGroup.value;
        try {
            this.loading = true;
            value['fecha_desde'] = this.datePipe.transform(value.fecha_desde, 'yyyy-MM-dd');
            value['fecha_hasta'] = this.datePipe.transform(value.fecha_hasta, 'yyyy-MM-dd');
            const res = await this.reporteService.informeNotificacion( value );
            if ( res.ok ) {
                this.items = (res.resp || []).map((item: Record<string, unknown>) => this.mapInformeItem(item));
                await this.sumUp();
                this.buildChart();
                if ( this.items.length === 0 ) {
                    this.messageService.add({severity: 'info', 'summary': 'Sin resultados'})
                }
            }
            this.loading = false;
        } catch (error: any) {
        }
    }

    async sumUp() {
        this.totalEnviado = 0;
        this.totalNoEnviado = 0;
        this.totalPendiente = 0;
        this.totalImporte = 0;
        for (const detalle of this.items as InformeNotificacionItem[]) {
            this.totalEnviado += Number(detalle.total_enviado);
            this.totalNoEnviado += Number(detalle.total_no_enviado);
            this.totalPendiente += Number(detalle.total_pendiente);
            this.totalImporte += Number(detalle.monto_total);
        }
    }

    async verDetalle(item: InformeNotificacionItem) {
        if (!this.canVerDetalle(item)) {
            return;
        }

        if (!item.id) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Detalle no disponible',
                detail: 'El backend debe retornar el id del historial para consultar el detalle por destinatario.',
            });
            return;
        }

        this.selectedInformeDetalle = item;
        this.detalleEstado = DETALLE_ESTADO.TODOS;
        this.detalleBusqueda = '';
        this.detalleFormGroup.patchValue({ estado: DETALLE_ESTADO.TODOS, busqueda: '' });
        this.detalleItems = [];
        this.detalleTotal = 0;
        this.showDialogDetalle = true;
        await this.cargarDetalleNotificacion();
    }

    async cargarDetalleNotificacion() {
        if (!this.selectedInformeDetalle?.id) {
            return;
        }

        try {
            this.loadingDetalle = true;
            this.detalleEstado = this.detalleFormGroup.get('estado')?.value || DETALLE_ESTADO.TODOS;
            this.detalleBusqueda = String(this.detalleFormGroup.get('busqueda')?.value || '').trim();
            const value = this.formGroup.value;
            const filtros = {
                id_empresa: value.id_empresa,
                estado: this.detalleEstado,
                busqueda: this.detalleBusqueda,
                page: 1,
                limit: 100,
            };
            const res = await this.reporteService.informeNotificacionDetalle(this.selectedInformeDetalle.id, filtros);

            if (res.ok) {
                const detalle = this.resolveDetalleResponse(res.resp);
                this.detalleItems = detalle.map((detalleItem) => this.mapDetalleItem(detalleItem));
                this.detalleTotal = this.resolveDetalleTotal(res.resp, this.detalleItems.length);

                if (this.detalleItems.length === 0) {
                    this.messageService.add({ severity: 'info', summary: 'Sin detalle', detail: 'No se encontraron destinatarios para el filtro seleccionado.' });
                }
            } else {
                this.detalleItems = [];
                this.detalleTotal = 0;
                this.messageService.add({ severity: 'warn', summary: 'No se pudo obtener detalle', detail: res.msg || 'Verifique el endpoint de detalle.' });
            }
        } catch (error) {
            this.detalleItems = [];
            this.detalleTotal = 0;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo obtener el detalle de notificaciones.' });
        } finally {
            this.loadingDetalle = false;
        }
    }

    canVerDetalle(item: InformeNotificacionItem): boolean {
        return item.detalle_disponible !== false && item.origen !== 'Reactivación';
    }

    async limpiarBusquedaDetalle() {
        this.detalleBusqueda = '';
        this.detalleFormGroup.get('busqueda')?.setValue('');
        await this.cargarDetalleNotificacion();
    }

    async onDetalleEstadoChange() {
        this.detalleEstado = this.detalleFormGroup.get('estado')?.value || DETALLE_ESTADO.TODOS;
        await this.cargarDetalleNotificacion();
    }

    private buildChart() {
        const groupedItems = new Map<string, { cantidad: number; importe: number; etiqueta: string }>();
        const sortedItems = [...(this.items as InformeNotificacionItem[])].sort((itemA, itemB) => {
            return new Date(itemA.fecha).getTime() - new Date(itemB.fecha).getTime();
        });

        for (const item of sortedItems) {
            const dateKey = this.datePipe.transform(item.fecha, 'yyyy-MM-dd');
            const label = this.datePipe.transform(item.fecha, 'dd/MM/yyyy');

            if (!dateKey || !label) {
                continue;
            }

            const currentItem = groupedItems.get(dateKey) ?? { cantidad: 0, importe: 0, etiqueta: label };
            currentItem.cantidad += Number(item.total_enviado);
            currentItem.importe += Number(item.monto_total);
            groupedItems.set(dateKey, currentItem);
        }

        const labels = Array.from(groupedItems.values()).map((item) => item.etiqueta);
        const cantidadPorFecha = Array.from(groupedItems.values()).map((item) => item.cantidad);
        const importePorFecha = Array.from(groupedItems.values()).map((item) => item.importe);

        if (labels.length === 0) {
            this.chartData = null;
            this.chartOptions = null;
            return;
        }

        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color') || '#495057';
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#6c757d';
        const numberFormatter = new Intl.NumberFormat('es-PY', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
        const quantityFormatter = new Intl.NumberFormat('es-PY', {
            maximumFractionDigits: 0,
        });

        this.chartData = {
            labels,
            datasets: [
                {
                    type: 'bar',
                    label: 'Cantidad enviada',
                    data: cantidadPorFecha,
                    backgroundColor: 'rgba(14, 116, 144, 0.78)',
                    borderColor: 'rgb(14, 116, 144)',
                    borderWidth: 1,
                    borderRadius: 8,
                    maxBarThickness: 42,
                    yAxisID: 'yCantidad'
                },
                {
                    type: 'line',
                    label: 'Importe total',
                    data: importePorFecha,
                    backgroundColor: 'rgba(249, 115, 22, 0.14)',
                    borderColor: 'rgb(249, 115, 22)',
                    borderWidth: 3,
                    tension: 0.3,
                    fill: false,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                    pointBackgroundColor: 'rgb(249, 115, 22)',
                    yAxisID: 'yImporte'
                }
            ]
        };

        this.chartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 1.8,
            layout: {
                padding: {
                    top: 8,
                    right: 8,
                    left: 4,
                    bottom: 0,
                }
            },
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                legend: {
                    position: 'top',
                    align: 'start',
                    labels: {
                        color: textColor,
                        usePointStyle: true,
                        boxWidth: 10,
                        boxHeight: 10,
                        padding: 16,
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (context: { dataset: { label?: string; yAxisID?: string }; parsed: { y: number } }) => {
                            const label = context.dataset.label ?? '';
                            const value = context.parsed.y;

                            if (context.dataset.yAxisID === 'yImporte') {
                                return `${label}: ${numberFormatter.format(value)}`;
                            }

                            return `${label}: ${quantityFormatter.format(value)}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                        maxRotation: 0,
                    },
                    grid: {
                        display: false,
                        drawBorder: false
                    }
                },
                yCantidad: {
                    type: 'linear',
                    position: 'left',
                    beginAtZero: true,
                    ticks: {
                        color: textColor,
                        precision: 0
                    },
                    grid: {
                        color: 'rgba(223, 231, 239, 0.7)',
                        drawBorder: false
                    }
                },
                yImporte: {
                    type: 'linear',
                    position: 'right',
                    beginAtZero: true,
                    ticks: {
                        color: textColor,
                        callback: (value: number | string) => {
                            return numberFormatter.format(Number(value));
                        }
                    },
                    grid: {
                        drawOnChartArea: false,
                        drawBorder: false
                    }
                }
            }
        };
    }

    async onEmpresa() {
        try {
            const value = this.formGroup.value;  
            let objeto = null;
            const filtros = [
                { field: 'id', value: value.id_empresa, type: 'id' },
                
            ]
            const res = await this.empresaService.getAll( Global.SIZE_GET_ALL, { filters: filtros } );
            if ( res.ok && res.resp.count > 0 ) {
                objeto = res.resp.rows[0];
            } else {
                this.messageService.add({severity: 'info', summary: 'Empresa no encontrada', detail: ''});
            }
            await this.setEmpresa(objeto);
        } catch (error: any) {
            this.messageService.add(this.objSession);
            setTimeout( () => { this.router.navigate(this.routerLogout) }, 4000);
        }
    }


    async setEmpresa( obj: Record<string, any> ) {
        this.formGroup.get('id_empresa').setValue(obj?.id);
        this.formGroup.get('nombre_empresa').setValue(obj?.nombre);
    }


    async cerrarOnLista( obj?: Record<string, any> ): Promise<void> {
        await super.cerrarOnLista( obj );
        if ( this.objClassOnLista === Empresa && obj ) {
            await this.setEmpresa(obj);
        }
        this.objClassOnLista = null;
    }
    
    async onListaEmpresa() {
        await super.onLista( this.empresaService, Empresa, Empresa.NOMBRE_PLURAL );
    }

    async descargar() {
        const pdf = new Pdf(this.currentUser);
        const value = this.formGroup.value;
        pdf.title = 'INFORME DE NOTIFICACIONES';
        pdf.headers = [
            { label: 'Fecha', name: 'fecha', type: 'date' },
            { label: 'Titulo', name: 'nombre', type: 'text' },
            { label: 'Descripcion', name: 'descripcion', type: 'text' },
            { label: 'Canal', name: 'canal', type: 'text' },
            { label: 'Origen', name: 'origen', type: 'text' },
            { label: 'Codigo de Usuario', name: 'codigo_usuario', type: 'text' },
            { label: 'Cantidad Enviada', name: 'total_enviado', type: 'number' },
            { label: 'Costo Unitario', name: 'costo_unitario', type: 'number' },
            { label: 'Monto Total', name: 'monto_total', type: 'number' },
        ]
        for (const item of this.items as InformeNotificacionItem[]) {
            pdf.addSource({ data: item });
        }
        pdf.foot = {
            headers: [
                { label: 'Total Enviados', name: 'total_enviados', type: 'number' },
                { label: 'Total Importe', name: 'total_importe', type: 'number' },
            ],
            source: {
                total_enviados: this.totalEnviado,
                total_importe: this.totalImporte,
            }
        }
        pdf.exportar();
    }

    async descargarExcel() {
        const datos: Record<string, any>[] = [];
        for (const item of this.items as InformeNotificacionItem[]) {
            datos.push({
                "Fecha": this.datePipe.transform(item.fecha, 'dd/MM/yyyy'),
                "Titulo": item.nombre,
                "Descripcion": item.descripcion,
                "Canal": item.canal,
                "Origen": item.origen,
                "Codigo de Usuario": item.codigo_usuario,
                "Cantidad Enviada": item.total_enviado,
                "Costo Unitario": item.costo_unitario,
                "Monto Total": item.monto_total,
            });
        }
        await this.xlsxService.exportAsExcelFile(datos, 'informe_notificacion');
    }

    private mapInformeItem(item: Record<string, unknown>): InformeNotificacionItem {
        return {
            id: this.resolveNumber(item['id']),
            fecha: item['fecha'] as Date | string,
            nombre: String(item['nombre'] || ''),
            descripcion: String(item['descripcion'] || ''),
            canal: this.resolveCanal(item),
            origen: String(item['origen'] || 'Campaña'),
            codigo_usuario: this.resolveCodigoUsuario(item),
            total_enviado: item['total_enviado'] as number | string,
            total_no_enviado: this.resolveNumber(item['total_no_enviado']),
            total_pendiente: this.resolvePendiente(item),
            costo_unitario: item['costo_unitario'] as number | string,
            monto_total: item['monto_total'] as number | string,
            tipo: item['tipo'] as number | string | undefined,
            detalle_disponible: item['detalle_disponible'] !== false,
            cod_creado_por: item['cod_creado_por'] as string | undefined,
            creado_por: item['creado_por'] as InformeNotificacionUsuario | undefined,
        };
    }

    private resolveDetalleResponse(resp: unknown): Record<string, unknown>[] {
        if (Array.isArray(resp)) {
            return resp as Record<string, unknown>[];
        }

        const response = resp as Record<string, unknown> | null;
        if (Array.isArray(response?.['rows'])) {
            return response['rows'] as Record<string, unknown>[];
        }

        if (Array.isArray(response?.['items'])) {
            return response['items'] as Record<string, unknown>[];
        }

        if (Array.isArray(response?.['data'])) {
            return response['data'] as Record<string, unknown>[];
        }

        return [];
    }

    private resolveDetalleTotal(resp: unknown, fallback: number): number {
        const response = resp as Record<string, unknown> | null;
        const total = Number(response?.['total'] ?? response?.['count'] ?? fallback);

        return Number.isNaN(total) ? fallback : total;
    }

    private mapDetalleItem(item: Record<string, unknown>): InformeNotificacionDetalleItem {
        const telefono = String(item['telefono'] || item['numero'] || item['destinatario'] || item['to_phone'] || '').trim();
        const estado = this.resolveEstadoDetalle(item);
        const motivo = String(item['motivo_error'] || item['error'] || item['provider_error_detail'] || item['motivo'] || '').trim();
        const error = estado === DETALLE_ESTADO.NO_ENVIADOS ? motivo : '';
        const destinatario = this.resolveDestinatarioDetalle(item, telefono);

        return {
            destinatario,
            telefono,
            destinatario_telefono: this.resolveDestinatarioTelefonoDetalle(destinatario, telefono),
            estado,
            estado_negocio: this.getDetalleEstadoLabel(estado),
            sid: String(item['sid'] || item['provider_message_sid'] || item['message_sid'] || '').trim(),
            status_proveedor: String(item['status_proveedor'] || item['status_twilio'] || item['provider_status_raw'] || '').trim(),
            error,
            fecha: (item['fecha'] || item['fecha_operacion'] || item['created_at'] || null) as Date | string | null,
            job_procesamiento_id: Number(item['job_procesamiento_id']) || null,
            id_empresa: Number(item['id_empresa']) || null,
        };
    }

    /**
     * Solo se reenvia lo que no llego, y solo si sabemos de que campania salio:
     * sin el job no hay plantilla ni mapeo de variables que reproducir.
     */
    esReenviable(detalle: InformeNotificacionDetalleItem): boolean {
        return detalle?.estado === DETALLE_ESTADO.NO_ENVIADOS
            && Boolean(detalle?.job_procesamiento_id)
            && Boolean(detalle?.telefono);
    }

    get haySeleccionParaReenviar(): boolean {
        return this.seleccionReenvio.length > 0;
    }

    /**
     * El reenvio nace como un job por campania, asi que una seleccion que
     * mezcla campanias no se puede resolver en una sola pasada.
     */
    private agruparSeleccionPorCampania(): Map<string, InformeNotificacionDetalleItem[]> {
        const porCampania = new Map<string, InformeNotificacionDetalleItem[]>();

        for (const detalle of this.seleccionReenvio) {
            const clave = `${detalle.job_procesamiento_id}-${detalle.id_empresa}`;
            porCampania.set(clave, [...(porCampania.get(clave) || []), detalle]);
        }

        return porCampania;
    }

    async onReenviarSeleccionados() {
        if (!this.haySeleccionParaReenviar || this.reenviando) {
            return;
        }

        const porCampania = this.agruparSeleccionPorCampania();

        if (porCampania.size > 1) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Seleccion de mas de una campania',
                detail: 'El reenvio se hace de a una campania por vez. Filtra por una y volve a intentar.',
                life: 8000,
            });
            return;
        }

        this.reenviando = true;

        try {
            const [detalles] = [...porCampania.values()];
            const idJob = Number(detalles[0].job_procesamiento_id);
            const idEmpresa = Number(detalles[0].id_empresa);
            const telefonos = detalles.map((detalle) => detalle.telefono);

            const resp = await this.jobService.reenviarDestinatarios(idJob, idEmpresa, telefonos);

            this.messageService.add({
                severity: resp.ok ? 'success' : 'warn',
                summary: resp.ok ? 'Reenvio encolado' : 'No se pudo reenviar',
                detail: resp.msg,
                life: 10000,
            });

            if (resp.ok) {
                this.seleccionReenvio = [];
                await this.cargarDetalleNotificacion();
            }
        } finally {
            this.reenviando = false;
        }
    }

    private resolveDestinatarioDetalle(item: Record<string, unknown>, telefono: string): string {
        const destinatario = String(item['destinatario'] || item['nombre'] || item['contacto'] || '').trim();
        if (destinatario) {
            return destinatario;
        }

        return telefono || 'Sin nombre';
    }

    private resolveDestinatarioTelefonoDetalle(destinatario: string, telefono: string): string {
        const nombre = String(destinatario || '').trim();
        const numero = String(telefono || '').trim();

        if (!nombre || nombre === numero || nombre === 'Sin nombre') {
            return numero || nombre || '-';
        }

        if (!numero) {
            return nombre;
        }

        return `${nombre} · ${numero}`;
    }

    getDetalleEstadoLabel(estado: string): string {
        switch (String(estado || '').toLowerCase()) {
            case DETALLE_ESTADO.ENVIADOS:
            case 'enviado':
            case 'sent':
            case 'delivered':
            case 'entregado':
            case 'read':
            case 'leído':
            case 'leido':
            case 'queued':
            case 'accepted':
            case 'sending':
                return 'Enviado';
            case DETALLE_ESTADO.NO_ENVIADOS:
            case 'no_enviado':
            case 'no enviado':
            case 'failed':
            case 'undelivered':
            case 'canceled':
                return 'No enviado';
            case DETALLE_ESTADO.PENDIENTES:
            case 'pendiente':
            case 'pendiente de confirmación':
                return 'Pendiente';
            default:
                return estado ? 'Pendiente' : '-';
        }
    }

    getDetalleEstadoClass(estado: string): string {
        switch (String(estado || '').toLowerCase()) {
            case DETALLE_ESTADO.ENVIADOS:
            case 'enviado':
            case 'sent':
            case 'delivered':
            case 'read':
            case 'queued':
            case 'accepted':
            case 'sending':
                return 'p-tag-success';
            case DETALLE_ESTADO.NO_ENVIADOS:
            case 'no_enviado':
            case 'no enviado':
            case 'failed':
            case 'undelivered':
            case 'canceled':
                return 'p-tag-danger';
            case DETALLE_ESTADO.PENDIENTES:
            case 'pendiente':
            case 'pendiente de confirmación':
                return 'p-tag-warning';
            default:
                return 'p-tag-info';
        }
    }

    private resolveCanal(item: Record<string, unknown>): string {
        const canal = String(item['canal'] || '').trim();
        if (canal) {
            return canal;
        }

        const tipo = Number(item['tipo'] ?? this.formGroup?.get('tipo')?.value);
        const tipoEncontrado = HistorialNotificacion.TIPOS.find((tipoItem) => tipoItem.valor === tipo);

        return tipoEncontrado?.nombre || '-';
    }

    private resolveCodigoUsuario(item: Record<string, unknown>): string {
        const codigoUsuario = String(item['codigo_usuario'] || item['cod_creado_por'] || '').trim();
        if (codigoUsuario) {
            return codigoUsuario;
        }

        const creadoPor = item['creado_por'] as InformeNotificacionUsuario | undefined;

        return String(creadoPor?.codigo || '').trim();
    }

    private isValidNotificationType(tipo: unknown): boolean {
        return HistorialNotificacion.TIPOS.some((item) => item.valor === Number(tipo));
    }

    private resolveNumber(value: unknown): number {
        const numberValue = Number(value || 0);
        return Number.isNaN(numberValue) ? 0 : numberValue;
    }

    private resolvePendiente(item: Record<string, unknown>): number {
        const pendiente = item['total_pendiente'] ?? item['total_pendientes'];
        return this.resolveNumber(pendiente);
    }

    private resolveEstadoDetalle(item: Record<string, unknown>): string {
        const status = String(item['status_proveedor'] || item['status_twilio'] || item['provider_status_raw'] || '').trim().toLowerCase();
        const error = String(item['error'] || item['provider_error_detail'] || item['motivo'] || '').trim();
        const sid = String(item['sid'] || item['provider_message_sid'] || item['message_sid'] || '').trim();
        const deliveryStatusCode = Number(item['delivery_status_code'] || 0);

        if (error || status === 'failed' || status === 'undelivered' || status === 'canceled') {
            return DETALLE_ESTADO.NO_ENVIADOS;
        }

        if (sid || deliveryStatusCode === 2 || status === 'sent' || status === 'delivered' || status === 'read') {
            return DETALLE_ESTADO.ENVIADOS;
        }

        return DETALLE_ESTADO.PENDIENTES;
    }

}
